import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

/** Floating always-on access to Blue — visible on every inmate page except the chat itself. */
export default function ChatbotWidget() {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname } = useLocation();

    if (pathname === "/inmate/chat") return null;

    return (
        <>
            {/* Floating button — sits above the bottom nav */}
            <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.94 }}
                onClick={() => setIsOpen((o) => !o)}
                data-testid="chatbot-widget-button"
                aria-label={isOpen ? "Close Blue chat menu" : "Talk with Blue"}
                className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full bg-white border border-border shadow-phoenix-deep flex items-center justify-center overflow-hidden"
            >
                <AnimatePresence mode="wait">
                    {isOpen ? (
                        <motion.div
                            key="close"
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 90, opacity: 0 }}
                        >
                            <X className="w-6 h-6 text-phoenix-navy" />
                        </motion.div>
                    ) : (
                        <motion.img
                            key="chat"
                            src="/brand/blue-avatar.png"
                            alt="Blue"
                            initial={{ rotate: 90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -90, opacity: 0 }}
                            className="w-full h-full object-cover rounded-full"
                        />
                    )}
                </AnimatePresence>
            </motion.button>

            {/* Popup card */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.96 }}
                        data-testid="chatbot-widget-popup"
                        className="fixed bottom-[168px] right-4 z-40 w-80 max-w-[calc(100vw-2rem)] bg-white border border-border rounded-3xl shadow-phoenix-float overflow-hidden"
                    >
                        <div className="bg-phoenix-soft/70 p-4 flex items-center gap-3">
                            <div className="w-11 h-11 rounded-2xl overflow-hidden bg-white border border-border flex items-center justify-center shrink-0">
                                <img src="/brand/blue-avatar.png" alt="Blue" className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <h3 className="font-display font-semibold text-sm text-phoenix-navy">Blue is here for you</h3>
                                <p className="text-xs text-muted-foreground">Your mental health companion</p>
                            </div>
                        </div>
                        <div className="p-4 space-y-3">
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                Start a conversation with Blue. I&apos;m here to listen, support, and help you build coping strategies.
                            </p>
                            <Link
                                to="/inmate/chat"
                                onClick={() => setIsOpen(false)}
                                data-testid="chatbot-widget-open-chat"
                                className="w-full px-4 py-3 bg-phoenix-navy text-white rounded-2xl text-center font-semibold hover:bg-phoenix-navy-2 transition-colors text-sm flex items-center justify-center gap-2"
                            >
                                <MessageCircle className="w-4 h-4" /> Talk with Blue
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
